// Canonical vocabulary for communication_logs.
//
// These columns are free text in Postgres — there is no CHECK constraint — and
// they have already drifted: production holds `email`/`phone`/`PHONE` and
// `Family`/`FAMILY`/`family` for the same meanings. The web read screen papers
// over it with a render-time `normalizeCommValue` shim, which hides the drift
// rather than preventing it.
//
// Both writers must agree on these tokens:
//   • apps/care-worker-app — normalises to UPPER_SNAKE before sending
//     (`normalizeEnumToken`), so current mobile writes are already canonical.
//   • apps/web — the coordinator intake form.
//
// A DB CHECK is deliberately NOT added here. The mobile app is a shipped binary
// that cannot be updated in lockstep with a migration, so a CHECK would turn any
// vocabulary drift into a hard failure on a live clinical write path. The CHECK
// belongs in a follow-up once an aligned mobile build has shipped, at which
// point these arrays move into regulated-enums.ts and gain a
// REGULATED_ENUM_CONTRACTS entry so the parity script enforces them.
//
// Until then: the values below are a SUPERSET of what the mobile pickers can
// produce, so widening the web form can never orphan a mobile write.

/** Mobile offers phone/email/in_person/video_call; the rest are web intake. */
export const COMMUNICATION_METHODS = [
  'PHONE',
  'EMAIL',
  'IN_PERSON',
  'VIDEO_CALL',
  'SMS',
  'LETTER',
  'PORTAL_MESSAGE',
  'FAX',
] as const;

/** Matches the mobile contact-type picker exactly. */
export const COMMUNICATION_CONTACT_TYPES = [
  'FAMILY',
  'GP',
  'SPECIALIST',
  'ALLIED_HEALTH',
  'GUARDIAN',
  'PHARMACY',
  'OTHER',
] as const;

export const COMMUNICATION_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const;

export type CommunicationMethod = (typeof COMMUNICATION_METHODS)[number];
export type CommunicationContactType = (typeof COMMUNICATION_CONTACT_TYPES)[number];
export type CommunicationDirection = (typeof COMMUNICATION_DIRECTIONS)[number];

/** Trim, collapse spaces/hyphens to underscore, upper-case. Mirrors the
 *  mobile `normalizeEnumToken` so both writers land on the same token. */
export function normaliseCommunicationToken(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const token = value
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
  return token === '' ? null : token;
}

export function isCommunicationMethod(value: unknown): value is CommunicationMethod {
  return typeof value === 'string' && (COMMUNICATION_METHODS as readonly string[]).includes(value);
}

export function isCommunicationContactType(value: unknown): value is CommunicationContactType {
  return (
    typeof value === 'string' && (COMMUNICATION_CONTACT_TYPES as readonly string[]).includes(value)
  );
}
